import React from 'react'
import { YapContentPreview } from '@yapstudios/yap-content-builder'
import { Section, Stack, Text, Image, Box, YapAssetImage, Gallery, Video, Calendar, Quiz, ActionButton, Info, ZStack } from '@yapstudios/yap-content-builder'
import { styles, fonts } from "./CBStyles.js"
import MessageCardComponent from './CollegeboardCardComponent.js'
import { collegeListComponents } from './CollegeboardCollegeListLibrary.js'
import { mapAriaHiddenArray } from './isAriaHidden.js'

export interface CBCollegeListPreviewProps {
    /**
     * Title of the page
     */
    title?: string;

    /**
     * Width to render the preview at
     */
    width: number;

    /**
     * Header for the tiles section
     */
    tilesHeader?: string;

    /**
     * Subheader for the tiles section
     */
    tilesHeaderSubheader?: string;

    /**
     * Tile items to render
     */
    tileItems?: any[];

    /**
     * List items to render
     */
    listItems?: any[];

    /**
     * List header
     */
    listHeader?: string;

    /**
     * Elements to hide from screen readers. Visible by default.
     */
    ariaHidden?: ('title' | 'tilesHeader' | 'tilesHeaderSubheader' | 'listHeader')[]
}


/** 
 * Presents a preview of an college list page
 * @component
 **/
function CBCollegeListPreview(props: CBCollegeListPreviewProps) : React.ReactNode {

    const ariaHiddenTilesMap = {
        'tilesHeader': 'title',
        'tilesHeaderSubheader': 'subtitle'
    }
    const ariaHiddenListMap = {
        'listHeader': 'title'
    }
    const ariaHiddenTitleMap = {
        'title': 'title'
    }

    let tileSection = [
        {
            'id': 'section-header',
            'type': 'section-header',
            'props': {
                title: props.tilesHeader ?? 'Interested in You',
                subtitle: props.tilesHeaderSubheader ?? 'Colleges that have contacted you',
                ariaHidden: mapAriaHiddenArray(ariaHiddenTilesMap, props.ariaHidden)
            }
        },

        {
            'id': 'college-tiles',
            'type': 'college-tiles',
            'props': {
                colleges: props.tileItems
            }
        },

    ]

    let listRows = (props.listItems ?? []).map(row => {
        return {
            'id': 'college-row',
            'type': row.type ?? 'college-row',
            'props': {
                title: row.title,
                subtitle: row.subtitle,
                logo: row.logo,
                ariaHidden: row.ariaHidden
            }
        }
    })


    let listSection = [{
        'id': 'section-header-your-list',
        'type': 'section-header',
        'props': {
            title: props.listHeader ?? 'Your List',
            ariaHidden: mapAriaHiddenArray(ariaHiddenListMap, props.ariaHidden)
        }
    }].concat(listRows)


    let sections: any[] = [
        {
            'id': 'large-header',
            'type': 'large-header',
            'props': {
                title: props.title ?? "Colleges",
                ariaHidden: mapAriaHiddenArray(ariaHiddenTitleMap, props.ariaHidden)
            }
        },

    ]

    if (props.tileItems) {
        sections = sections.concat(tileSection)
    }

    if (props.listItems) {
        sections = sections.concat(listSection)
    }

    let page = {
        id: 'page-1',
        sections: sections
    }

    return <YapContentPreview components={collegeListComponents} styles={styles} fonts={fonts} {...props} page={page} />
}


export interface CBCollegeListSavedProps {
    /**
     * Title
     */
    title: string;

    /**
     * Render width
     */
    width: number;

    /**
     * Header
     */
    tilesHeader: string;

    /**
     * Subheader
     */
    tilesHeaderSubheader: string;

    /**
     * Tile items
     */
    tileItems: any[];

    /**
     * List items
     */
    listItems: any[];

    /**
     * List header
     */
    listHeader: string;

    /**
     * 
     */
    ariaHidden?: ('title' | 'tilesHeader' | 'tilesHeaderSubheader' | 'listHeader')[]
}


/**
* Presents a preview of an college list page
* @component
*/
function CBSavedListPreview (props: CBCollegeListSavedProps) : React.ReactNode {

    const ariaHiddenTilesMap = {
        'tilesHeader': 'title',
        'tilesHeaderSubheader': 'subtitle'
    }
    const ariaHiddenListMap = {
        'listHeader': 'title'
    }
    const ariaHiddenTitleMap = {
        'title': 'title'
    }

    let tileSection = [
        {
            'id': 'section-header',
            'type': 'section-header',
            'props': {
                title: props.tilesHeader ?? 'Colleges',
                subtitle: props.tilesHeaderSubheader ?? "",
                ariaHidden: mapAriaHiddenArray(ariaHiddenTilesMap, props.ariaHidden)
            }
        },

        {
            'id': 'college-tiles',
            'type': 'college-tiles',
            'props': {
                colleges: (props.tileItems ?? []).map((tile, idx) => {
                    return { ...tile, placeholder: idx == 0 }
                })
            }
        },

    ]

    let listRows = (props.listItems ?? []).map((row, idx) => {
        return {
            'id': 'message-card',
            'type': row.type ?? 'message-card',
            'props': {
                ...row,
                title: row.title ?? '',
                subtitle: row.subtitle ?? '',
                logo: row.logo ?? '',
                ariaHidden: row.ariaHidden
            }
        }
    })


    let listSection = [{
        'id': 'section-header-your-list',
        'type': 'section-header',
        'props': {
            title: props.listHeader ?? 'Messages',
            ariaHidden: mapAriaHiddenArray(ariaHiddenListMap, props.ariaHidden)
        }
    }].concat(listRows)


    let sections: any[] = [
        {
            'id': 'large-header',
            'type': 'large-header',
            'props': {
                title: props.title ?? "Saved",
                ariaHidden: mapAriaHiddenArray(ariaHiddenTitleMap, props.ariaHidden)
            }
        },

    ]

    if (props.tileItems) {
        sections = sections.concat(tileSection)
    }

    if (props.listItems) {
        sections = sections.concat(listSection)
    }

    let page = {
        id: 'page-1',
        sections: sections
    }

    return <YapContentPreview components={collegeListComponents} styles={styles} fonts={fonts} {...props} width={props.width} page={page} />
}

export { CBCollegeListPreview, CBSavedListPreview }